import { Link } from "react-router-dom";
import { Leaf, ArrowLeft, Sun, Users, Moon, Award, Music, Play, Heart, Sparkles, CheckCircle2, Star, Globe, BookOpen, Mic } from "lucide-react";

export default function AboutFounder() {
  return (
    <div className="min-h-screen bg-spa-bone" data-testid="about-founder-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 md:px-12 lg:px-20 bg-spa-bone/90 backdrop-blur-md border-b border-spa-border/30">
        <Link to="/" className="flex items-center">
          <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-14 w-auto" />
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-spa-text-secondary hover:text-spa-moss transition-colors" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20 space-y-16">

        {/* Section 1: What Is the SuperAPP */}
        <section className="animate-fade-in" data-testid="about-what-is">
          <p className="overline-text mb-4">About Us</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-spa-moss mb-6 leading-tight" data-testid="about-title">
            What Is the <a href="/glossary#term-smahoo" className="text-spa-terracotta hover:underline">SMAHOO</a> <a href="/glossary#term-me-well" className="text-spa-terracotta hover:underline">ME.WELL</a> <a href="/glossary#term-mind-spa" className="text-spa-terracotta hover:underline">Mind-SPA</a> <a href="/glossary#term-sof-b" className="text-spa-terracotta hover:underline">Sof.B</a> and <a href="/glossary#term-well-b" className="text-spa-terracotta hover:underline">WELL.b</a> exPE SuperAPP?
          </h1>
          <div className="space-y-4 text-spa-text-secondary leading-relaxed">
            <p>
              The SMAHOO ME.WELL Mind-SPA Sof.B and WELL.b exPE SuperAPP is a comprehensive daily self-care hygiene system created by <strong className="text-spa-moss">M. Shirvington A. Hannays</strong> to help individuals practice mental health as a state of being and well-being as a daily lifestyle.
            </p>
            <p>
              It is the first digital ecosystem designed to operationalize the World Health Organization's definition of mental health into simple, guided, culturally grounded, faith-aligned daily practices.
            </p>
            <p>This SuperAPP integrates four original wellness frameworks into one unified experience:</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="spa-card border-l-4 border-l-spa-terracotta">
              <p className="font-serif text-lg text-spa-moss mb-1"><a href="/glossary#term-smahoo" className="hover:text-spa-terracotta">SMAHOO</a></p>
              <p className="text-sm text-spa-text-secondary">Self Motivation And Healing Ourselves Online</p>
            </div>
            <div className="spa-card border-l-4 border-l-spa-sage">
              <p className="font-serif text-lg text-spa-moss mb-1"><a href="/glossary#term-me-well" className="hover:text-spa-terracotta">ME.WELL</a></p>
              <p className="text-sm text-spa-text-secondary">Mind-Emotional Wellness</p>
            </div>
            <div className="spa-card border-l-4 border-l-spa-sky">
              <p className="font-serif text-lg text-spa-moss mb-1"><a href="/glossary#term-mind-spa" className="hover:text-spa-terracotta">Mind-SPA</a> <a href="/glossary#term-sof-b" className="hover:text-spa-terracotta">Sof.B</a></p>
              <p className="text-sm text-spa-text-secondary">State of Being</p>
            </div>
            <div className="spa-card border-l-4 border-l-spa-clay">
              <p className="font-serif text-lg text-spa-moss mb-1"><a href="/glossary#term-well-b" className="hover:text-spa-terracotta">WELL.b</a> exPE</p>
              <p className="text-sm text-spa-text-secondary">Wellness Experience</p>
            </div>
          </div>
          <p className="text-spa-text-secondary mt-6 leading-relaxed italic">
            Together, they form a daily emotional hygiene system for real people living real lives.
          </p>
        </section>

        {/* Divider */}
        <div className="border-t border-spa-border/50" />

        {/* Section 2: Why the SuperAPP Was Created */}
        <section data-testid="about-why-created">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-6">Why the SuperAPP Was Created</h2>
          <p className="text-spa-text-secondary leading-relaxed mb-6">The SuperAPP was created to solve a global problem:</p>

          <div className="spa-card bg-gradient-to-r from-spa-terracotta/5 to-transparent border-l-4 border-l-spa-terracotta mb-8">
            <p className="font-serif text-lg text-spa-moss italic">
              "People know mental health is important, but they don't know how to practice it daily."
            </p>
          </div>

          <p className="text-spa-text-secondary leading-relaxed mb-4">The system provides:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Guided emotional hygiene",
              "Daily self-care rituals",
              "State-of-being check-ins",
              "Culturally grounded wellness practices",
              "Faith-aligned inner stillness",
              "Digital support for healing and motivation",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-spa-border/30">
                <CheckCircle2 className="w-5 h-5 text-spa-sage flex-shrink-0" />
                <span className="text-sm text-spa-moss">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-spa-text-secondary mt-6 leading-relaxed font-medium">
            It transforms mental health from a crisis response into a daily lifestyle.
          </p>
        </section>

        {/* Divider */}
        <div className="border-t border-spa-border/50" />

        {/* Section 3: The Four Pillars */}
        <section data-testid="about-four-pillars">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-8">The Four Pillars Inside the SuperAPP</h2>
          <div className="space-y-6">
            {[
              {
                num: "1",
                title: "SMAHOO",
                subtitle: "Self Motivation And Healing Ourselves Online",
                desc: "A framework for activating inner motivation, emotional regulation, and self-healing.",
                color: "bg-spa-terracotta",
                glossary: "/glossary#term-smahoo"
              },
              {
                num: "2",
                title: "ME.WELL",
                subtitle: "Mind-Emotional Wellness",
                desc: "A daily emotional hygiene system that teaches people how to maintain inner balance.",
                color: "bg-spa-sage",
                glossary: "/glossary#term-me-well"
              },
              {
                num: "3",
                title: "Mind-SPA Sof.B",
                subtitle: "State of Being",
                desc: "A digital sanctuary for stillness, reflection, grounding, and inner restoration.",
                color: "bg-spa-sky",
                glossary: "/glossary#term-mind-spa"
              },
              {
                num: "4",
                title: "WELL.b exPE",
                subtitle: "Wellness Experience",
                desc: "A lived experience model that turns well-being into a daily, embodied practice.",
                color: "bg-spa-clay",
                glossary: "/glossary#term-well-b"
              },
            ].map((pillar) => (
              <div key={pillar.num} className="spa-card flex items-start gap-5" data-testid={`pillar-${pillar.num}`}>
                <div className={`w-12 h-12 ${pillar.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white font-serif text-xl">{pillar.num}</span>
                </div>
                <div>
                  <a href={pillar.glossary} className="font-serif text-xl text-spa-moss hover:text-spa-terracotta transition-colors">{pillar.title}</a>
                  <p className="text-sm text-spa-terracotta font-medium mb-1">{pillar.subtitle}</p>
                  <p className="text-sm text-spa-text-secondary leading-relaxed">{pillar.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-spa-border/50" />

        {/* Section 4: What the SuperAPP Helps People Do */}
        <section data-testid="about-helps-people">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-6">What the SuperAPP Helps People Do</h2>
          <p className="text-spa-text-secondary leading-relaxed mb-6">The SuperAPP guides individuals to:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Regulate their emotional state",
              "Practice daily mental hygiene",
              "Build inner resilience",
              "Cultivate stillness and clarity",
              "Align with WHO's definition of mental health",
              "Live well as a daily discipline",
              "Heal themselves through guided micro-practices",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-spa-border/30">
                <Heart className="w-5 h-5 text-spa-terracotta flex-shrink-0 mt-0.5" />
                <span className="text-sm text-spa-moss">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-spa-text-secondary mt-6 leading-relaxed">
            It is simple, accessible, non-clinical, and culturally resonant.
          </p>
        </section>

        {/* Divider */}
        <div className="border-t border-spa-border/50" />

        {/* Section 5: Who Created the SuperAPP */}
        <section data-testid="about-creator">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-6">Who Created the SuperAPP?</h2>
          <p className="text-spa-text-secondary leading-relaxed mb-6">The SuperAPP was created by:</p>

          <div className="spa-card bg-gradient-to-br from-spa-terracotta/5 via-white to-spa-sage/5 mb-8">
            <div className="flex items-center gap-5 mb-4">
              <div className="w-20 h-20 bg-spa-terracotta rounded-full flex items-center justify-center text-white font-serif text-3xl flex-shrink-0">
                R
              </div>
              <div>
                <h3 className="font-serif text-2xl text-spa-moss" data-testid="founder-name">M. Shirvington A. Hannays</h3>
                <p className="text-spa-terracotta font-medium">Professionally known as <a href="/glossary#term-mr--rapsoul-r" className="hover:underline">Mr. RAPsoul.R</a> — The Laughing Drama Therapist</p>
              </div>
            </div>
          </div>

          <p className="text-spa-text-secondary leading-relaxed mb-4">He is a:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { text: "Wellness innovator", icon: Sparkles },
              { text: "Mental-emotional health educator", icon: BookOpen },
              { text: "Pioneer & trailblazer (Outliers Edge, 2024)", icon: Star },
              { text: "Spotify-recognized rising star podcaster (2025)", icon: Mic },
              { text: "Creator of the RAPsoul.R philosophy", icon: Heart },
              { text: "Architect of emotional hygiene as a lifestyle", icon: Globe },
            ].map(({ text, icon: Icon }) => (
              <div key={text} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-spa-border/30">
                <Icon className="w-5 h-5 text-spa-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-sm text-spa-moss">{text}</span>
              </div>
            ))}
          </div>
          <p className="text-spa-text-secondary mt-6 leading-relaxed italic">
            His work is rooted in culture, faith, emotional restoration, and digital empowerment.
          </p>
        </section>

        {/* Divider */}
        <div className="border-t border-spa-border/50" />

        {/* Section 6: What Makes the SuperAPP Unique */}
        <section data-testid="about-unique">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-6">What Makes the SuperAPP Unique?</h2>
          <p className="text-spa-text-secondary leading-relaxed mb-6">The SuperAPP is the first system in the world to:</p>
          <div className="space-y-3">
            {[
              "Operationalize the WHO definition of mental health",
              "Integrate cultural identity and faith-based grounding",
              "Provide daily emotional hygiene practices",
              "Combine digital tools with inner healing",
              "Unify four original wellness frameworks",
              "Offer a guided, accessible, non-clinical approach",
              "Transform mental health into a state of being",
            ].map((item) => (
              <div key={item} className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-white to-spa-sage/5 border border-spa-border/30">
                <div className="w-8 h-8 bg-spa-sage/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-spa-sage" />
                </div>
                <span className="text-sm text-spa-moss font-medium">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 spa-card bg-gradient-to-r from-spa-terracotta/5 to-transparent border-l-4 border-l-spa-terracotta">
            <p className="font-serif text-xl text-spa-moss italic">
              "It is not just an app — it is a living ecosystem."
            </p>
          </div>
        </section>

        {/* Stats Grid */}
        <div className="border-t border-spa-border/50 pt-16" data-testid="about-stats">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="spa-card text-center">
              <Sun className="w-8 h-8 text-spa-terracotta mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">100+</p>
              <p className="text-sm text-spa-text-secondary">Wellness Sessions</p>
            </div>
            <div className="spa-card text-center">
              <Users className="w-8 h-8 text-spa-sage mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">5000+</p>
              <p className="text-sm text-spa-text-secondary">Social Network Followers</p>
            </div>
            <div className="spa-card text-center">
              <Moon className="w-8 h-8 text-spa-sky mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">24/7</p>
              <p className="text-sm text-spa-text-secondary">AI Support</p>
            </div>
            <div className="spa-card text-center">
              <Award className="w-8 h-8 text-spa-clay mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">WHO</p>
              <p className="text-sm text-spa-text-secondary">Aligned Practices</p>
            </div>
            <div className="spa-card text-center">
              <Music className="w-8 h-8 text-spa-terracotta mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">17+</p>
              <p className="text-sm text-spa-text-secondary">Spotify Streaming Countries</p>
            </div>
            <div className="spa-card text-center">
              <Play className="w-8 h-8 text-spa-sky mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-spa-moss mb-1">LIVE</p>
              <p className="text-sm text-spa-text-secondary">The Laughing Therapist OnCALL</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-4 pb-8">
          <Link to="/" className="spa-button-primary inline-flex items-center gap-2 px-8 py-3" data-testid="about-cta">
            Start Your Wellness Journey <Sparkles className="w-4 h-4" />
          </Link>
          <p className="text-xs text-spa-text-secondary mt-4">
            <Link to="/glossary" className="text-spa-terracotta hover:underline">View Glossary of Terms</Link> to learn more about the SMAHOO ecosystem.
          </p>
        </div>
      </div>
    </div>
  );
}
